// The solar price list editor (docs/12-solar-roof-scan.md §3.1, §9.3). An
// installed system costs
//   base price + panels × price per panel + the inverter for that size (+ battery)
// and homeowners see it as a range of ±10%, rounded to £50, including
// installation and VAT. The installer fills in:
//   - their system prices: base price, price per panel, the panel they fit
//     (wattage and model) and the smallest system they offer (in panels)
//   - inverter steps: the inverter fitted to systems up to N kWp and its price
//     (an empty price = that step isn't offered; the next priced one up is used)
//   - an optional battery (label, price, share of generation used at home)
// Used by the solar Pricing tab (SolarPricingPage) and the new-component
// wizard's "Your prices" step; the parent owns the state and saves it with
// api.updateWidget(id, { solarPricing }).
//
// Every number is edited as text (SolarForm), so half-typed values don't fight
// the number parsing; solarProblems() checks the text the way the API does, and
// fromSolarForm() turns it into a SolarPricing. Checks (mirrored by the API):
//   panel wattage      300–700 W            smallest system  1–60 panels
//   base price         £0–£50,000           price per panel  £20–£5,000
//   inverter price     £50–£20,000          battery price    £100–£20,000
//   inverter steps     1–8 rows, "up to" strictly ascending, 0.5–50 kWp
//   smallest system    minPanels × panel kW must fit the largest step
//   self-consumption   50–95%
// Empty prices are allowed (null = not set yet); the widget can only quote
// once base, per panel and at least one inverter have a price (isPriced).
//
// SolarPriceExample (below the editor) shows "What homeowners see" for a few
// system sizes, worked out with solarPrice.ts (the same formula the API prices with).
import { Plus, Trash2 } from "lucide-react";
import type { SolarPricing } from "@widget";
import { SOLAR_SPREAD, batteryOffered, isPriced, kwp, maxPanelsPriced, solarPriceRange } from "../solarPrice";
import { Toggle } from "./controls";
import "../editor.css";
import "../pricing.css";
import "../solarPricing.css";

// ─── Limits (mirrored by the API) ────────────────────────────────────────────

export const SOLAR_LIMITS = {
  panelWatts: [300, 700] as const,
  panels: [1, 60] as const,
  basePrice: [0, 50000] as const,
  panelPrice: [20, 5000] as const,
  inverterPrice: [50, 20000] as const,
  batteryPrice: [100, 20000] as const,
  upToKwp: [0.5, 50] as const,
  inverters: [1, 8] as const,
  selfConsumptionPct: [50, 95] as const,
  panelModel: 60,
  inverterLabel: 40,
  batteryLabel: 40,
};

// ─── Editable state ──────────────────────────────────────────────────────────

/** One inverter step as typed. */
export interface InverterRow {
  label: string; // shown to homeowners
  upTo: string; // kWp, e.g. "4.4"
  price: string; // digits only; "" = not offered
}

/** The whole price list as typed. */
export interface SolarForm {
  panelWatts: string;
  panelModel: string;
  basePrice: string; // "" = not set
  panelPrice: string; // "" = not set
  minPanels: string;
  inverters: InverterRow[];
  battery: { enabled: boolean; label: string; price: string; selfConsumptionPct: string };
}

const text = (n: number | null) => (n === null ? "" : String(n));

/** Saved prices as editable text. */
export function toSolarForm(p: SolarPricing): SolarForm {
  return {
    panelWatts: String(p.panelWatts),
    panelModel: p.panelModel,
    basePrice: text(p.basePrice),
    panelPrice: text(p.panelPrice),
    minPanels: String(p.minPanels),
    inverters: p.inverters.map((i) => ({ label: i.label, upTo: String(i.upToKwp), price: text(i.price) })),
    battery: {
      enabled: p.battery.enabled,
      label: p.battery.label,
      price: text(p.battery.price),
      selfConsumptionPct: String(Math.round(p.battery.selfConsumption * 100)),
    },
  };
}

// ─── Parsing and checks ──────────────────────────────────────────────────────

const gbp = (n: number) => `£${n.toLocaleString("en-GB")}`;
/** A whole number as typed, or null. */
const whole = (t: string) => (/^\d+$/.test(t.trim()) ? Number(t.trim()) : null);
/** A kWp figure as typed (up to 2 decimal places), or null. */
const decimal = (t: string) => (/^\d+(\.\d{1,2})?$/.test(t.trim()) ? Number(t.trim()) : null);
const between = (n: number | null, [lo, hi]: readonly [number, number]) => n !== null && n >= lo && n <= hi;
/** Allow for floating-point kWp (e.g. 10 × 0.435) when comparing with a step. */
const KWP_EPS = 1e-9;

/** "" is fine (not set / not offered); otherwise a whole £ within the limits. */
function priceProblem(t: string, limits: readonly [number, number]): string | null {
  return t === "" || between(whole(t), limits) ? null : `Enter ${gbp(limits[0])} to ${gbp(limits[1])}`;
}

/** What's wrong with each field (null = fine). */
export interface SolarProblems {
  panelWatts: string | null;
  basePrice: string | null;
  panelPrice: string | null;
  minPanels: string | null;
  rows: { label: string | null; upTo: string | null; price: string | null }[];
  inverters: string | null; // the list as a whole (row count)
  batteryPrice: string | null;
  selfConsumption: string | null;
  /** How many fields need fixing before it can be saved. */
  count: number;
}

/** Check the form the way the API will. */
export function solarProblems(f: SolarForm): SolarProblems {
  const L = SOLAR_LIMITS;
  const watts = whole(f.panelWatts);
  const panelWatts = between(watts, L.panelWatts) ? null : `Enter ${L.panelWatts[0]} to ${L.panelWatts[1]} W`;

  // Inverter steps: each "up to" in range and strictly above the row before it
  const rows = f.inverters.map((r, i) => {
    const up = decimal(r.upTo);
    let upTo = between(up, L.upToKwp) ? null : `Enter ${L.upToKwp[0]} to ${L.upToKwp[1]} kWp`;
    const prev = i > 0 ? decimal(f.inverters[i - 1].upTo) : null;
    if (!upTo && prev !== null && up! <= prev + KWP_EPS) upTo = `Must be more than ${prev} kWp (the row above)`;
    const price = priceProblem(r.price, L.inverterPrice);
    // Every step needs a name (it is shown to homeowners when offered)
    const label = r.label.trim() ? null : "Name the inverter homeowners will see";
    return { label, upTo, price };
  });
  const n = f.inverters.length;
  const inverters = n < L.inverters[0] || n > L.inverters[1] ? `Add ${L.inverters[0]} to ${L.inverters[1]} inverter steps` : null;

  // Smallest system: 1–60 panels, and it has to fit the largest inverter step
  const mp = whole(f.minPanels);
  let minPanels = between(mp, L.panels) ? null : `Enter ${L.panels[0]} to ${L.panels[1]} panels`;
  const largest = n ? decimal(f.inverters[n - 1].upTo) : null;
  if (!minPanels && !panelWatts && largest !== null) {
    const size = (mp! * watts!) / 1000;
    if (size > largest + KWP_EPS) {
      minPanels = `${mp} panels is ${size.toFixed(2)} kWp: more than your largest inverter step (${largest} kWp)`;
    }
  }

  const pct = whole(f.battery.selfConsumptionPct);
  const selfConsumption = between(pct, L.selfConsumptionPct) ? null : `Enter ${L.selfConsumptionPct[0]} to ${L.selfConsumptionPct[1]}%`;

  const out = {
    panelWatts,
    basePrice: priceProblem(f.basePrice, L.basePrice),
    panelPrice: priceProblem(f.panelPrice, L.panelPrice),
    minPanels,
    rows,
    inverters,
    batteryPrice: priceProblem(f.battery.price, L.batteryPrice),
    selfConsumption,
  };
  const count =
    [out.panelWatts, out.basePrice, out.panelPrice, out.minPanels, out.inverters, out.batteryPrice, out.selfConsumption].filter(Boolean).length +
    rows.reduce((k, r) => k + (r.label ? 1 : 0) + (r.upTo ? 1 : 0) + (r.price ? 1 : 0), 0);
  return { ...out, count };
}

/** The form as a SolarPricing (only meaningful when solarProblems(f).count === 0). */
export function fromSolarForm(f: SolarForm): SolarPricing {
  return {
    panelWatts: whole(f.panelWatts) ?? 0,
    panelModel: f.panelModel.trim(),
    basePrice: whole(f.basePrice),
    panelPrice: whole(f.panelPrice),
    minPanels: whole(f.minPanels) ?? 0,
    inverters: f.inverters.map((r) => ({ label: r.label.trim(), upToKwp: decimal(r.upTo) ?? 0, price: whole(r.price) })),
    battery: {
      enabled: f.battery.enabled,
      label: f.battery.label.trim(),
      price: whole(f.battery.price),
      selfConsumption: (whole(f.battery.selfConsumptionPct) ?? 0) / 100,
    },
  };
}

/** Base, per panel and at least one inverter priced: what the widget needs to quote. */
export const hasSolarPrices = (f: SolarForm) => isPriced(fromSolarForm(f));

// ─── Small inputs ────────────────────────────────────────────────────────────

/** Keep digits only: accepts "11,500" or "£11500" as typed. */
const digits = (v: string) => v.replace(/[^\d]/g, "");
/** Keep digits and one decimal point (kWp). */
const decimals = (v: string) => v.replace(/[^\d.]/g, "").replace(/(\..*)\./g, "$1");
/** Show a typed whole number with thousands separators. */
const pretty = (t: string) => (t !== "" && /^\d+$/.test(t) ? Number(t).toLocaleString("en-GB") : t);

/** A £ field: "£" prefix, digits only, thousands separators, inline error below. */
function MoneyField(props: {
  id: string;
  label: string;
  hint?: string;
  value: string;
  error: string | null;
  placeholder?: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="solar-field">
      <label htmlFor={props.id}>{props.label}</label>
      <div className={`price-input${props.error ? " is-bad" : ""}`}>
        <span aria-hidden="true">£</span>
        <input
          id={props.id}
          inputMode="numeric"
          placeholder={props.placeholder ?? "Not set"}
          value={pretty(props.value)}
          onChange={(e) => props.onChange(digits(e.target.value))}
        />
      </div>
      {props.error ? <span className="price-error">{props.error}</span> : props.hint && <span className="solar-hint">{props.hint}</span>}
    </div>
  );
}

// ─── The editor ──────────────────────────────────────────────────────────────

export function SolarPriceEditor({ form, onChange }: { form: SolarForm; onChange: (next: SolarForm) => void }) {
  const problems = solarProblems(form);
  const set = (patch: Partial<SolarForm>) => onChange({ ...form, ...patch });
  const setBattery = (patch: Partial<SolarForm["battery"]>) => onChange({ ...form, battery: { ...form.battery, ...patch } });
  const setRow = (i: number, patch: Partial<InverterRow>) =>
    onChange({ ...form, inverters: form.inverters.map((r, j) => (j === i ? { ...r, ...patch } : r)) });

  // A new step starts 2 kWp above the last one, with no price yet (not offered)
  const lastUp = decimal(form.inverters[form.inverters.length - 1]?.upTo ?? "") ?? 0;
  const canAdd = form.inverters.length < SOLAR_LIMITS.inverters[1] && lastUp < SOLAR_LIMITS.upToKwp[1];
  function addRow() {
    const upTo = Math.min(Math.round((lastUp + 2) * 10) / 10, SOLAR_LIMITS.upToKwp[1]);
    onChange({ ...form, inverters: [...form.inverters, { label: "", upTo: String(upTo), price: "" }] });
  }

  // How many panels a step covers at the current wattage (a hint in each row)
  const watts = whole(form.panelWatts);
  function panelsUpTo(r: InverterRow): string {
    const up = decimal(r.upTo);
    if (!watts || up === null) return "—";
    return `up to ${Math.floor((up * 1000) / watts + KWP_EPS)} panels`;
  }

  return (
    <div className="solar-editor">
      {/* ── Your system prices ─────────────────────────────────────────────── */}
      <h3 className="solar-subhead">Your system prices</h3>
      <div className="solar-panel-row">
        <MoneyField
          id="solar-base"
          label="Base price"
          hint="Installation: scaffolding, labour, mounting, DNO and MCS paperwork"
          value={form.basePrice}
          error={problems.basePrice}
          onChange={(basePrice) => set({ basePrice })}
        />
        <MoneyField
          id="solar-per-panel"
          label="Price per panel"
          hint="Each panel supplied and fitted"
          value={form.panelPrice}
          error={problems.panelPrice}
          onChange={(panelPrice) => set({ panelPrice })}
        />
      </div>
      <div className="solar-panel-row">
        <div className="solar-field">
          <label htmlFor="solar-watts">Panel wattage</label>
          <div className={`price-input solar-unit${problems.panelWatts ? " is-bad" : ""}`}>
            <input id="solar-watts" inputMode="numeric" value={form.panelWatts} onChange={(e) => set({ panelWatts: digits(e.target.value) })} />
            <span aria-hidden="true">W</span>
          </div>
          {problems.panelWatts && <span className="price-error">{problems.panelWatts}</span>}
        </div>
        <div className="solar-field solar-field-grow">
          <label htmlFor="solar-model">Panel model (shown to homeowners)</label>
          <input
            id="solar-model"
            className="input"
            value={form.panelModel}
            maxLength={SOLAR_LIMITS.panelModel}
            placeholder="e.g. 435 W all-black panels"
            onChange={(e) => set({ panelModel: e.target.value })}
          />
        </div>
        <div className="solar-field">
          <label htmlFor="solar-min">Smallest system</label>
          <div className={`price-input solar-unit solar-unit-wide${problems.minPanels ? " is-bad" : ""}`}>
            <input id="solar-min" inputMode="numeric" value={form.minPanels} onChange={(e) => set({ minPanels: digits(e.target.value) })} />
            <span aria-hidden="true">panels</span>
          </div>
          {problems.minPanels && <span className="price-error solar-error-wide">{problems.minPanels}</span>}
        </div>
      </div>

      {/* ── Inverters ──────────────────────────────────────────────────────── */}
      <h3 className="solar-subhead">Inverters</h3>
      <p className="solar-note">
        The inverter you fit for each system size. A system gets the first step it fits in; leave a price empty to not offer that step (the
        next one up is used). Your largest step sets the biggest system offered.
      </p>
      <div className="solar-table-wrap">
        <table className="price-table solar-table">
          <thead>
            <tr>
              <th scope="col">Inverter (shown to homeowners)</th>
              <th scope="col">For systems up to</th>
              <th scope="col">Price</th>
              <th scope="col" aria-label="Remove" />
            </tr>
          </thead>
          <tbody>
            {form.inverters.map((r, i) => {
              const p = problems.rows[i];
              return (
                <tr key={i} className={r.price === "" ? "is-off" : ""}>
                  {/* Name shown to homeowners */}
                  <td>
                    <input
                      className={`input solar-inverter${p.label ? " is-bad" : ""}`}
                      aria-label={`Inverter ${i + 1}: name shown to homeowners`}
                      placeholder="e.g. 3.68 kW inverter"
                      maxLength={SOLAR_LIMITS.inverterLabel}
                      value={r.label}
                      onChange={(e) => setRow(i, { label: e.target.value })}
                    />
                    {p.label && <span className="price-error">{p.label}</span>}
                  </td>
                  {/* Upper system size in kWp, with the panel count it covers */}
                  <td>
                    <div className={`price-input solar-unit solar-unit-wide${p.upTo ? " is-bad" : ""}`}>
                      <input
                        type="number"
                        step="0.1"
                        min={SOLAR_LIMITS.upToKwp[0]}
                        max={SOLAR_LIMITS.upToKwp[1]}
                        inputMode="decimal"
                        aria-label={`Inverter ${i + 1}: for systems up to (kWp)`}
                        value={r.upTo}
                        onChange={(e) => setRow(i, { upTo: decimals(e.target.value) })}
                      />
                      <span aria-hidden="true">kWp</span>
                    </div>
                    {p.upTo ? <span className="price-error">{p.upTo}</span> : <span className="solar-hint">{panelsUpTo(r)}</span>}
                  </td>
                  {/* Price; empty = not offered */}
                  <td>
                    <div className={`price-input${p.price ? " is-bad" : ""}`}>
                      <span aria-hidden="true">£</span>
                      <input
                        inputMode="numeric"
                        aria-label={`Inverter ${i + 1}: price`}
                        placeholder="Not offered"
                        value={pretty(r.price)}
                        onChange={(e) => setRow(i, { price: digits(e.target.value) })}
                      />
                    </div>
                    {p.price && <span className="price-error">{p.price}</span>}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="icon-btn solar-remove"
                      aria-label={`Remove inverter ${i + 1}`}
                      title="Remove this step"
                      disabled={form.inverters.length <= SOLAR_LIMITS.inverters[0]}
                      onClick={() => onChange({ ...form, inverters: form.inverters.filter((_, j) => j !== i) })}
                    >
                      <Trash2 size={15} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="solar-table-foot">
        <button type="button" className="btn btn-secondary" onClick={addRow} disabled={!canAdd}>
          <Plus size={16} aria-hidden="true" /> Add an inverter
        </button>
        <span className="muted">
          {form.inverters.length} of {SOLAR_LIMITS.inverters[1]} steps.
        </span>
        {problems.inverters && <span className="price-error">{problems.inverters}</span>}
      </div>

      {/* ── Battery ────────────────────────────────────────────────────────── */}
      <h3 className="solar-subhead">Battery</h3>
      <div className="grant-row">
        <div>
          <div className="grant-label">Offer a battery</div>
          <div className="grant-hint">Homeowners can add it to any system. It needs a price to be offered.</div>
        </div>
        <Toggle id="solar-battery" checked={form.battery.enabled} ariaLabel="Offer a battery" onChange={(enabled) => setBattery({ enabled })} />
      </div>
      {form.battery.enabled && (
        <div className="solar-panel-row">
          <div className="solar-field solar-field-grow">
            <label htmlFor="solar-battery-label">Battery (shown to homeowners)</label>
            <input
              id="solar-battery-label"
              className="input"
              maxLength={SOLAR_LIMITS.batteryLabel}
              placeholder="e.g. 5 kWh battery"
              value={form.battery.label}
              onChange={(e) => setBattery({ label: e.target.value })}
            />
          </div>
          <MoneyField
            id="solar-battery-price"
            label="Price added"
            placeholder="Not offered"
            value={form.battery.price}
            error={problems.batteryPrice}
            onChange={(price) => setBattery({ price })}
          />
          <div className="solar-field">
            <label htmlFor="solar-battery-sc">Used at home with it</label>
            <div className={`price-input solar-unit${problems.selfConsumption ? " is-bad" : ""}`}>
              <input
                id="solar-battery-sc"
                inputMode="numeric"
                value={form.battery.selfConsumptionPct}
                onChange={(e) => setBattery({ selfConsumptionPct: digits(e.target.value) })}
              />
              <span aria-hidden="true">%</span>
            </div>
            {problems.selfConsumption ? (
              <span className="price-error">{problems.selfConsumption}</span>
            ) : (
              <span className="solar-hint">Without a battery we assume 50%.</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Worked example: what homeowners see ─────────────────────────────────────

/** The explainer line shown above the worked example. */
export const SOLAR_PRICE_EXPLAINER = `Price = base + panels × per panel + the inverter for that size (+ battery). Homeowners see it as a range of ±${Math.round(
  SOLAR_SPREAD * 100,
)}%, rounded to £50, including installation and VAT.`;

/**
 * A few system sizes priced with the widget's own maths: the smallest system,
 * about 8 and 12 panels, and the most the inverter steps cover. Shows nothing
 * useful (a short note) until the form is valid and priced.
 */
export function SolarPriceExample({ form }: { form: SolarForm }) {
  const valid = solarProblems(form).count === 0;
  const p = fromSolarForm(form);
  if (!valid || !isPriced(p)) {
    return <p className="solar-note">Set a base price, a price per panel and at least one inverter price to see what homeowners see.</p>;
  }

  // Sizes to show: smallest, ~8, ~12 and the largest covered, in order, without repeats
  const max = maxPanelsPriced(p);
  const sizes = [...new Set([p.minPanels, 8, 12, max])].filter((n) => n >= p.minPanels && n <= max).sort((a, b) => a - b);
  const battery = batteryOffered(p) ? p.battery.price : null;

  return (
    <div className="solar-example">
      <div className="solar-table-wrap">
        <table className="price-table solar-example-table">
          <caption className="solar-subhead">What homeowners see</caption>
          <thead>
            <tr>
              <th scope="col">Panels</th>
              <th scope="col">Size</th>
              <th scope="col">Inverter</th>
              <th scope="col">Price</th>
              <th scope="col">Range shown</th>
            </tr>
          </thead>
          <tbody>
            {sizes.map((n) => {
              const r = solarPriceRange(n, false, p);
              return (
                <tr key={n}>
                  <th scope="row">{n}</th>
                  <td>{kwp(n, p).toFixed(2)} kWp</td>
                  <td>{r ? r.inverter.label || "—" : "Not offered"}</td>
                  <td className="solar-num">{r ? gbp(r.price) : "—"}</td>
                  <td className="solar-num solar-range">{r ? `${gbp(r.low)} – ${gbp(r.high)}` : "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {battery !== null && (
        <p className="solar-note">
          With the {p.battery.label || "battery"}, add {gbp(battery)} before the range is worked out.
        </p>
      )}
    </div>
  );
}
